import { WakeUp } from 'common/interface/props';
import { useState } from 'react';
import Swal from 'sweetalert2';

import Container from 'components/UI/atoms/Container';
import Grid from 'components/UI/atoms/Grid';
import Button from 'components/UI/atoms/Button';
import TextField from 'components/UI/atoms/TextField';
import Table from 'components/UI/atoms/Table';
import Select from 'components/UI/atoms/Select';
import Pagination from 'components/UI/molecules/Pagination';
import ToggleSwitch from 'components/UI/atoms/ToggleSwitch';

import { useMutation, useQuery } from 'react-query';
import { partnerSchedulerList, partnerSchedulerDeactivate, partnerSchedulerActivate } from 'components/form/api/partner-admin.api';
import { PartnerSchedulerUserItem } from 'components/form/interfaces/partner-admin.interface';

interface IUserAdminMemberLookup {
  solType: WakeUp;
}

/**
 * @alias 멤버조회 탭 컴포넌트
 * @description 11번가 스케줄러 연동 멤버 목록 조회 및 스케줄러 활성화/해지
 */
export default function UserAdminMemberLookup({ solType }: IUserAdminMemberLookup) {
  const [nowPage, setNowPage] = useState(1);
  const [viewCount] = useState(30);
  const [searchType, setSearchType] = useState<string>('email');
  const [searchValue, setSearchValue] = useState('');
  const [appliedSearch, setAppliedSearch] = useState<{ searchType: string; searchValue: string }>({
    searchType: 'email',
    searchValue: '',
  });

  const {
    data: listResponse,
    isLoading: isListLoading,
    refetch: refetchList,
  } = useQuery({
    queryKey: ['partnerSchedulerList', solType, nowPage, viewCount, appliedSearch],
    queryFn: () =>
      partnerSchedulerList({
        solType,
        searchType: appliedSearch.searchValue ? appliedSearch.searchType : '',
        searchValue: appliedSearch.searchValue || '',
        viewCount,
        nowPage,
      }),
    enabled: !!solType,
    keepPreviousData: true,
  });

  /**
   * @alias 스케줄러 활성화/해지 뮤테이션 콜백 생성
   * @description API 성공/실패 시 Swal 팝업 표시 및 리스트 리페치
   */
  const createMutationHandlers = (isActivate: boolean) => ({
    onSuccess: (data: { success: boolean; message?: string }) => {
      if (data.success) {
        Swal.fire({
          title: '성공',
          html: isActivate ? '스케줄러가 활성화되었습니다.' : '스케줄러가 해지되었습니다.',
          icon: 'success',
        });
        refetchList();
      } else {
        Swal.fire({
          title: '실패',
          html: data.message || (isActivate ? '스케줄러 활성화에 실패했습니다.' : '스케줄러 해지에 실패했습니다.'),
          icon: 'error',
        });
      }
    },
    onError: (error: any) => {
      Swal.fire({
        title: '실패',
        html: error?.response?.data?.message || error?.message || (isActivate ? '스케줄러 활성화에 실패했습니다.' : '스케줄러 해지에 실패했습니다.'),
        icon: 'error',
      });
    },
  });

  const deactivateMutation = useMutation(partnerSchedulerDeactivate, createMutationHandlers(false));
  const activateMutation = useMutation(partnerSchedulerActivate, createMutationHandlers(true));

  const memberList: PartnerSchedulerUserItem[] = listResponse?.result || [];
  const totalLength = listResponse?.totalLength || 0;
  const maxPage = Math.ceil(totalLength / viewCount) || 1;

  /**
   * @alias 검색 이벤트 핸들러
   * @description 적용된 검색 조건 갱신 후 1페이지로 이동
   */
  const handleSearch = () => {
    setAppliedSearch({ searchType, searchValue });
    setNowPage(1);
  };

  /**
   * @alias 스케줄러 사용 여부 토글 핸들러
   * @description useYn true 시 해지 API, false 시 활성화 API 호출
   */
  const handleToggleUseYn = async (user: PartnerSchedulerUserItem) => {
    if (user.useYn) {
      // OFF: 해지(비활성화)
      const result = await Swal.fire({
        title: '스케줄러 해지',
        html: '스케줄러를 해지(비활성화) 하시겠습니까?',
        icon: 'question',
        showCancelButton: true,
        cancelButtonText: '취소',
        confirmButtonText: '확인',
      });
      if (result.isConfirmed) {
        deactivateMutation.mutate(user.userId);
      }
    } else {
      // ON: 재등록(활성화)
      const result = await Swal.fire({
        title: '스케줄러 활성화',
        html: '스케줄러를 다시 활성화 하시겠습니까?',
        icon: 'question',
        showCancelButton: true,
        cancelButtonText: '취소',
        confirmButtonText: '확인',
      });
      if (result.isConfirmed) {
        activateMutation.mutate(user.userId);
      }
    }
  };

  return (
    <Container noBody={true}>
      {/* 검색 필터 - 조회 탭과 동일한 UI/UX */}
      <Grid
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={10}
        bottom={20}
        top={40}
        style={{
          flexFlow: 'row wrap',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '10px',
            flexFlow: 'row wrap',
          }}
        >
          <Select label="검색어" value={searchType} onChange={(e) => setSearchType(e.target.value)} variant="outlined" width={160}>
            <option value="email">이메일</option>
            <option value="wakeupId">11번가 아이디</option>
          </Select>
          <TextField
            type="text"
            placeholder="조회 할 내용"
            variant="outlined"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button
            variant="outlined"
            color="main"
            size="small"
            width={57}
            height={35}
            borderRadius={4}
            onClick={handleSearch}
            style={{
              width: '57px',
              padding: '7px 3px',
            }}
          >
            검색
          </Button>
        </div>
      </Grid>

      <Grid>
        <Table
          rowHeight={52}
          showRowCount={true}
          allRowCount={totalLength}
          loading={isListLoading}
          noRowsLabel="조회된 멤버가 없습니다."
          headerBackgroundColor="#F7F7F7"
          headerItems={[
            {
              width: 70,
              headerName: 'No.',
              align: 'center',
              headerAlign: 'center',
            },
            {
              flex: 1,
              minWidth: 150,
              headerName: '이메일',
              align: 'center',
              headerAlign: 'center',
            },
            {
              flex: 1,
              minWidth: 120,
              headerName: '11번가 아이디',
              align: 'center',
              headerAlign: 'center',
            },
            {
              flex: 1,
              minWidth: 100,
              headerName: '스케줄러 사용 여부',
              align: 'center',
              headerAlign: 'center',
              renderCell: (params: any) => {
                /** @alias 사용 여부 컬럼 셀 렌더 - 토글 스위치 표시 */
                const row = params.row;
                const user = memberList.find((m) => m.userId === row.id);
                if (!user) return null;
                return (
                  <ToggleSwitch
                    isChecked={user.useYn}
                    onChange={() => handleToggleUseYn(user)}
                    width="36px"
                    height="20px"
                    circleWidth="16px"
                    circleHeight="16px"
                    sliderColor="#0072CE"
                  />
                );
              },
            },
          ]}
          bodyItems={memberList.map((item, index) => {
            const no = totalLength - ((nowPage - 1) * viewCount + index);
            return [no, item.email, item.wakeupId || '-', item.useYn];
          })}
          rowUniqueIds={memberList.map((item) => item.userId)}
        />
        <Grid top={30} />
        <Pagination nowPage={nowPage} max={maxPage} size={5} nowPageEvent={setNowPage} />
      </Grid>
    </Container>
  );
}
